import { OrderProduct } from '@app/components/orders/OrderProduct/OrderProduct';
import { useOrderProductsContext } from '@app/components/orders/OrderProducts/OrderProductsContext';
import { Notification } from '@app/components/various/Notification';
import { NotificationAction } from '@app/components/various/Notification/NotificationAction';
import { getActiveDeliveryWindowsIdsForOrderBuyer } from '@app/ducks';
import { getOrderDetails, removeProductsFromOrder } from '@app/ducks/order';
import { getHumanTimePeriod } from '@app/logic/deliveryWindows';
import {
  getIsProductInOrderByDelwinIds,
  getProductsCountInDelwin,
  getProductsToRemoveByDeliveryWindows,
  getTotalPriceInDelwin,
  getUnitsCountInDelwin,
} from '@app/logic/Orders';
import { formatPriceWithCurrencyAffix } from '@app/logic/price';
import { getAllOrderedVariants } from '@app/logic/products';
import { useConfirmationGuard } from '@app/utils/hooks';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { alphabeticSortByKey } from '@app/utils/objectSort';
import { DeliveryWindow } from '@typings';
import { prop, uniqBy } from 'ramda';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { OrderProductsGroupHeader } from '../OrderProductsGroupHeader';
import { OrderProductsGroupHeaderItem } from '../OrderProductsGroupHeaderItem';
import { OrderProductsGroupHeaderItems } from '../OrderProductsGroupHeaderItems';

interface Props {
  deliveryWindow: DeliveryWindow.Mixed;
}

export const OrderDeliveryWindowGroup = ({ deliveryWindow }: Props) => {
  const dispatch = useDispatch();
  const orderDetails = useSelector(getOrderDetails);
  const { sortBySKU } = useOrderProductsContext();
  const confirmationGuard = useConfirmationGuard();
  const deliveryWindowId = deliveryWindow.deliveryWindow;
  const totalPrice = getTotalPriceInDelwin(deliveryWindowId, orderDetails.order.products);
  const { t } = useTranslation(['common', 'totals', 'products', 'expiredDelwins', 'confirmationConfig']);
  const delwinVariants = React.useMemo(
    () =>
      Object.values(orderDetails.products)
        .flatMap(getAllOrderedVariants)
        .filter(product => getIsProductInOrderByDelwinIds(product.product, [deliveryWindowId], orderDetails.order.products)),
    [deliveryWindowId, orderDetails.order.products, orderDetails.products],
  );
  const activeDeliveryWindowsIds = useSelector(getActiveDeliveryWindowsIdsForOrderBuyer);
  const isExpired = !activeDeliveryWindowsIds.includes(deliveryWindowId);

  const uniqueVariants = React.useMemo(() => uniqBy(prop('product'), delwinVariants), [delwinVariants]);
  const variants = React.useMemo(
    () => (sortBySKU ? alphabeticSortByKey(uniqueVariants, 'sku') : uniqueVariants),
    [sortBySKU, uniqueVariants],
  );
  const { deliveryPeriod } = getHumanTimePeriod(deliveryWindow);

  const removeProducts = confirmationGuard(() => ({
    content: t('confirmationConfig:remove_variants_by_delwin.content', {
      names: deliveryWindow.name,
    }),
    onOk: () => {
      const products = getProductsToRemoveByDeliveryWindows(orderDetails.order, [deliveryWindowId]);
      dispatch(removeProductsFromOrder({ deliveryWindows: [deliveryWindowId], products }));
    },
    title: t('confirmationConfig:remove_variants_by_delwin.title_other'),
  }));

  if (isEmpty(variants)) {
    return null;
  }

  const productCountInDelwin = getProductsCountInDelwin(deliveryWindowId, orderDetails.order.products);
  const unitCountInDelwin = getUnitsCountInDelwin(deliveryWindowId, orderDetails.order.products);

  return (
    <div>
      <OrderProductsGroupHeader
        isDisabled={isExpired}
        bottomContent={
          isExpired && (
            <div className="mt-2">
              <Notification
                isBordered
                type="warning"
                message={t('expiredDelwins:delivery_window_has_expired_one', { name: deliveryWindow.name })}
              >
                <NotificationAction onClick={removeProducts}>
                  {t('confirmationConfig:remove_variants_by_delwin.title_other')}
                </NotificationAction>
              </Notification>
            </div>
          )
        }
      >
        <OrderProductsGroupHeaderItems>
          <div>
            <div className="heading-3">{deliveryWindow.name}</div>
            <div className="text-neutral-60 heading-4">{t('common:delivery_window_one')}</div>
          </div>
          {isDefined(deliveryPeriod) && (
            <div className="shrink-0">
              <div className="heading-3">{deliveryPeriod}</div>
              <div className="text-neutral-60 heading-4">{t('common:delivery')}</div>
            </div>
          )}
        </OrderProductsGroupHeaderItems>
        <OrderProductsGroupHeaderItems>
          <OrderProductsGroupHeaderItem value={productCountInDelwin} label={t('products:product', { count: productCountInDelwin })} />
          <OrderProductsGroupHeaderItem value={unitCountInDelwin} label={t('common:unit', { count: unitCountInDelwin })} />
          <OrderProductsGroupHeaderItem
            value={formatPriceWithCurrencyAffix(orderDetails.currency)(totalPrice)}
            label={t('totals:total_one')}
          />
        </OrderProductsGroupHeaderItems>
      </OrderProductsGroupHeader>
      <div className="space-y-5">
        {variants.map(variant => (
          <OrderProduct deliveryWindowIds={[deliveryWindowId]} key={variant.product} productInfo={variant} />
        ))}
      </div>
    </div>
  );
};
